import * as React from "react";
import styled from "styled-components";
import { AttachmentErrorType, IAttachmentError, IAttachmentInputProps } from ".";
import { FileProcessingStatus, IAttachmentFile } from "..";
import { Button, IButtonProps } from "../../../../components/Buttons/Button";
import { WizardContext } from "../../../../components/Wizard/Context/WizardContext";
import { AttachmentContext } from "../../../../context/AttachmentContext/AttachmentContext";
import { strings } from "../../../../localisation";
import { ISendEmailDataModel } from "../../../EmailWizard/models/SendEmail";
import { SendEmailExecuteModel } from "../../../EmailWizard/models/SendEmail/SendEmailExecuteModel";
import { AttachmentList } from "../AttachmentList";
import { AttachmentErrors } from "./AttachmentErrors/AttachmentErrors";
import { AttachmentCloseClickDiv, AttachmentInputButtonDiv } from "./AttachmentInput.styles";

const FileInput = styled.input`
    display: none;
`;

export const AttachmentInput = React.memo(({ FormikProps }: IAttachmentInputProps) => {

    // Context
    const attachmentContext = React.useContext(AttachmentContext);
    const { IsCompleting } = React.useContext(WizardContext);

    // Refs
    const attachmentInputRef = React.createRef<HTMLDivElement>();
    const fileUploadRef = React.createRef<HTMLInputElement>();

    // State
    const [showAttachments, setShowAttachments] = React.useState(false);
    const [uploadErrors, setUploadErrors] = React.useState<IAttachmentError[]>([]);
    const [visibleAttachmentCount, setVisibleAttachmentCount] = React.useState<number>(0);

    React.useEffect(() => {
        FormikProps?.setFieldValue(nameof.full<SendEmailExecuteModel<ISendEmailDataModel>>((m) => m.formDataModel.Attachments), attachmentContext.Attachments);

        const visibleAttachments = attachmentContext.Attachments
            .filter((attachment) => attachment.ProcessingStatus !== FileProcessingStatus.Deleted)
            ?? [];

        setVisibleAttachmentCount(visibleAttachments.length);
    }, [attachmentContext.Attachments.filter((att) => att.ProcessingStatus !== FileProcessingStatus.Deleted).length]);

    const toggleShowAttachments = () => {
        if (visibleAttachmentCount > 0) {
            setShowAttachments(!showAttachments);
        } else {
            handleAddAttachment();
        }
    };

    const handleAddAttachment = (() => {
        if (fileUploadRef?.current) {
            fileUploadRef.current.click();
        }
    });

    const handleClickOutside = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!attachmentInputRef?.current?.contains(e.target as Node)) {
            setShowAttachments(false);
        }
    };

    const handleFilesChanged = ((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.currentTarget.files) {

            const validFiles = validateFiles(e.currentTarget.files);

            if (validFiles.length) {
                attachmentContext.UploadAttachments(validFiles);
                if (!showAttachments) {
                    setShowAttachments(true);
                }
            }
        }

        if (fileUploadRef.current) {
            fileUploadRef.current.value = "";
        }
    });

    const validateFiles = (files: FileList): IAttachmentFile[] => {
        const validFiles: IAttachmentFile[] = [];
        const duplicateFiles: IAttachmentFile[] = [];
        const invalidFiles: IAttachmentFile[] = [];

        Array.from(files).forEach((file) => {
            if (attachmentContext.Attachments.find((attachment) => attachment.ProcessingStatus === FileProcessingStatus.Uploaded && attachment.File.name === file.name)) {
                // Duplicate name
                duplicateFiles.push({ File: file, ProcessingStatus: FileProcessingStatus.Errored });
            } else if (file.size > (8 * 1024 * 1024)) {
                // Over 8MB
                invalidFiles.push({ File: file, ProcessingStatus: FileProcessingStatus.Errored });
            } else {
                validFiles.push({ File: file, ProcessingStatus: FileProcessingStatus.Pending });
            }
        });

        onUploadError(duplicateFiles, invalidFiles);

        return validFiles;
    };

    const onUploadError = (duplicateFiles: IAttachmentFile[], invalidFiles: IAttachmentFile[]) => {
        if (duplicateFiles.length || invalidFiles.length) {

            const duplicateErrors = duplicateFiles.map<IAttachmentError>((err) => {
                return { Attachment: err, ErrorType: AttachmentErrorType.Duplicate };
            });
            const invalidErrors = invalidFiles.map<IAttachmentError>((err) => {
                return { Attachment: err, ErrorType: AttachmentErrorType.Size };
            });

            setUploadErrors(duplicateErrors.concat(invalidErrors));
        }
    };

    const attachmentButtonProps: IButtonProps = {
        ariaLabel: visibleAttachmentCount ? `${strings.Label_Attachments_Button} (${visibleAttachmentCount})` : strings.Action_Attachments_Add,
        children: visibleAttachmentCount ? `${strings.Label_Attachments_Button} (${visibleAttachmentCount})` : strings.Action_Attachments_Add,
        icon: "Paperclip",
        variant: visibleAttachmentCount ? "Tertiary" : "Primary",
        onClick: toggleShowAttachments,
        disabled: IsCompleting,
    };

    return (
        <div>
            {showAttachments &&
                <AttachmentCloseClickDiv onClick={handleClickOutside} />
            }

            <div ref={attachmentInputRef}>
                {uploadErrors.length > 0 &&
                    <AttachmentErrors Errors={uploadErrors} OnClose={() => setUploadErrors([])}/>
                }

                <AttachmentInputButtonDiv>
                    <Button {...attachmentButtonProps} />
                </AttachmentInputButtonDiv>

                <AttachmentList
                    Attachments={attachmentContext.Attachments}
                    OnAddAttachment={handleAddAttachment}
                    OnRemoveAttachment={attachmentContext.RemoveAttachment}
                    FormikProps={FormikProps}
                    Visible={visibleAttachmentCount > 0 && showAttachments} />

                <FileInput
                    ref={fileUploadRef}
                    accept="*"
                    multiple
                    type="file"
                    aria-label={strings.Label_Attachments_FileInput}
                    onChange={handleFilesChanged}
                />

            </div>
        </div>
    );
}, () => true);
